import { useState, useLayoutEffect, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import html2canvas from "html2canvas";
import { EXPORT_CONFIG } from "./constants";
import { CAPTURE_TRANSLATIONS } from "../../Language/types";
import useValidatedTranslation from "../../Hooks/useValidatedTranslation";
import RezultataiClass from "../../clases/rezultatai";

const SUPPORTED_LANGUAGES = ["en", "lt", "et", "lv", "ru"];

// Data processing hook
/**
 * Process results data for charts and tables
 * @param {Array} info - Raw data array
 * @param {Array} tipai - Types configuration array
 * @param {Object} filtrai - Filter configuration object
 * @returns {Object} Processed data object containing atsakymai, puslapiai, and visiAts
 */
export const useRezultataiProcessor = (info, tipai, filtrai) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Hooks must be called unconditionally at the top level
  const rezultatai = useMemo(
    () =>
      new RezultataiClass({
        nerodyti: {
          pages: ["0. welcome"],
          questions: [],
        },
      }),
    []
  );

  // Effect to adjust 'nerodyti' based on filters
  useEffect(() => {
    if (filtrai && Object.keys(filtrai).length > 0) {
      try {
        // Ensure 'rezultatai' object exists before calling methods
        if (rezultatai) {
          rezultatai.addToNerodytiPages(
            "7. Socialinės ir demografinės charakteristikos"
          );
        }
      } catch (err) {
        console.error("Error updating nerodyti pages based on filters:", err);
        setError("Failed to update filter exclusions.");
      }
    }
    // Optionally, reset if filters are removed? Depends on desired logic.
  }, [filtrai, rezultatai]); // Dependencies: filtrai and rezultatai instance

  // Memoized data processing logic
  const processedData = useMemo(() => {
    // Perform validation inside the memo, ensuring hooks above are always called
    if (!Array.isArray(info)) {
      console.error("useRezultataiProcessor: info must be an array", {
        received: typeof info,
      });
      setError("Invalid data format");
      return { atsakymai: {}, puslapiai: {}, visiAts: {} };
    }
    if (!Array.isArray(tipai)) {
      console.error("useRezultataiProcessor: tipai must be an array", {
        received: typeof tipai,
      });
      setError("Invalid types format");
      return { atsakymai: {}, puslapiai: {}, visiAts: {} };
    }
    // Filtrai validation might be less strict, or handled by filtering logic itself
    // if (filtrai && typeof filtrai !== 'object') {
    //     console.error('useRezultataiProcessor: filtrai must be an object', { received: typeof filtrai });
    //     setError('Invalid filters format');
    //     return { atsakymai: {}, puslapiai: {}, visiAts: {} };
    // }

    // Proceed only if inputs seem valid (basic check)
    if (!tipai?.length || !info?.length || !rezultatai)
      return {
        atsakymai: {},
        puslapiai: {},
        visiAts: {},
      };

    setIsLoading(true); // Indicate processing start
    try {
      const atsakymai = rezultatai.atsakymaiGrafikui(info);
      const puslapiai = rezultatai.puslapiaiIsTipu(tipai);
      const visiAts = rezultatai.beMetu(
        rezultatai.klausimuAtsakymai(info, tipai)
      );

      // Commented out debug log remains commented
      /* ... */

      return {
        atsakymai,
        puslapiai,
        visiAts,
      };
    } catch (err) {
      console.error("Error processing RezultataiTable data:", {
        error: err,
        infoLength: info?.length,
        tipaiLength: tipai?.length,
        hasFilters: filtrai && Object.keys(filtrai).length > 0,
        errorMessage: err.message,
        errorStack: err.stack,
      });
      setError(err.message || "Error processing data");
      return {
        atsakymai: {},
        puslapiai: {},
        visiAts: {},
      };
    } finally {
      // Ensure loading is set to false even if errors occur
      setIsLoading(false);
    }
  }, [info, tipai, filtrai, rezultatai]); // Dependencies updated

  // Input validation logging (moved after hooks)
  // These logs are for awareness; actual handling is now within useMemo
  if (!Array.isArray(info)) {
    console.error("Validation Check Failed: info must be an array", {
      received: typeof info,
    });
  }
  if (!Array.isArray(tipai)) {
    console.error("Validation Check Failed: tipai must be an array", {
      received: typeof tipai,
    });
  }
  if (filtrai && typeof filtrai !== "object") {
    console.error("Validation Check Failed: filtrai must be an object", {
      received: typeof filtrai,
    });
  }

  // Return the combined state
  return {
    ...processedData,
    isLoading,
    error,
  };
};

// Language selection hook
export const usePlotLanguage = () => {
  const { i18n } = useTranslation();

  return useMemo(() => {
    const language = i18n.language;
    // If language is not supported, fallback to 'default' (English)
    return SUPPORTED_LANGUAGES.includes(language) ? language : "default";
  }, [i18n.language]);
};

// Validate languages at startup in development
if (process.env.NODE_ENV !== "production") {
  console.info("Supported languages:", SUPPORTED_LANGUAGES);
}

// Scroll handling hook - labiau optimizuota versija su idempotentine logika
export const useScrollToQuestion = (
  klausimas,
  ref,
  setKlausimas,
  setTipas,
  activeQuestionId,
  setActiveQuestionId
) => {
  useLayoutEffect(() => {
    if (!klausimas || !ref.current) return;

    // Tikriname ar jau neesame aktyvavę šio klausimo, kad išvengtume kartotinių scrollų
    if (activeQuestionId === klausimas) return;

    // Nustatome aktyvaus klausimo ID, kad nesukeltume papildomų scrollų
    if (setActiveQuestionId && typeof setActiveQuestionId === "function") {
      setActiveQuestionId(klausimas);
    }

    // Paieškome elemento su konkrečiu ID, jei įmanoma
    const targetElement = document.getElementById(`question-${klausimas}`);

    if (targetElement) {
      // Jei turime konkretu elementą, skroliname į jį
      targetElement.scrollIntoView({ behavior: "smooth", block: "center" });
    } else if (ref.current) {
      // Atsarginis variantas - skroliname į bendrą konteinerį
      ref.current.scrollIntoView({ behavior: "smooth" });
    }

    // Valome klausimo duomenis po skrolinimo
    if (setKlausimas && typeof setKlausimas === "function") {
      setKlausimas(null);
    }
    if (setTipas && typeof setTipas === "function") {
      setTipas(null);
    }


  }, [
    klausimas,
    ref,
    setKlausimas,
    setTipas,
    activeQuestionId,
    setActiveQuestionId,
  ]);
};

// MOVED: useContainerElement was moved to separate file to avoid circular dependencies

// Capture handling hook
export const useHandleCapture = () => {
  const { vt } = useValidatedTranslation(CAPTURE_TRANSLATIONS);

  const handler = useCallback(
    async (element, title) => {
      if (!element) {
        const error = vt("element_not_found");
        console.error(error);
        throw new Error(error);
      }

      try {
        element.classList.add("download-mode");
        await new Promise((resolve) => setTimeout(resolve, 500));

        const rect = element.getBoundingClientRect();
        const chartWrapper = element.querySelector(".recharts-wrapper");
        const legend = element.querySelector(".recharts-legend-wrapper");
        const isRadialChart = element.querySelector(".recharts-pie") !== null;

        const { width, height } = calculateDimensions(
          rect,
          chartWrapper,
          legend,
          isRadialChart
        );

        const canvas = await html2canvas(element, {
          scale: EXPORT_CONFIG.scale,
          useCORS: true,
          logging: false,
          width,
          height,
          backgroundColor: EXPORT_CONFIG.backgroundColor,
          onclone: (clonedDoc) =>
            setupClonedElement(clonedDoc, width, height, isRadialChart),
        });

        element.classList.remove("download-mode");
        downloadImage(canvas, title);
      } catch (err) {
        element.classList.remove("download-mode");
        console.error(vt("error_generating_image"), err);
        throw err;
      }
    },
    [vt]
  );

  return handler;
};

// Helper functions
const calculateDimensions = (rect, chartWrapper, legend, isRadialChart) => {
  if (!chartWrapper) {
    return {
      width: Math.max(rect.width, EXPORT_CONFIG.minWidth),
      height: Math.max(rect.height, EXPORT_CONFIG.minHeight),
    };
  }

  const chartRect = chartWrapper.getBoundingClientRect();
  const legendRect = legend ? legend.getBoundingClientRect() : { height: 0 };

  const totalHeight = isRadialChart
    ? chartRect.height +
      legendRect.height +
      EXPORT_CONFIG.topPadding / 2 +
      EXPORT_CONFIG.bottomPadding +
      EXPORT_CONFIG.legendMarginTop
    : chartRect.height +
      legendRect.height +
      EXPORT_CONFIG.topPadding +
      EXPORT_CONFIG.bottomPadding +
      EXPORT_CONFIG.legendMarginTop;

  const totalWidth = Math.max(
    chartRect.width + EXPORT_CONFIG.sidePadding * 2,
    legendRect.width + EXPORT_CONFIG.sidePadding * 2
  );

  return {
    width: Math.max(totalWidth, EXPORT_CONFIG.minWidth),
    height: Math.max(totalHeight, EXPORT_CONFIG.minHeight),
  };
};

const setupClonedElement = (clonedDoc, width, height, isRadialChart) => {
  const clonedElement = clonedDoc.body.querySelector(
    ".question-content-wrapper"
  );
  if (!clonedElement) return;

  clonedElement.querySelectorAll("*").forEach((el) => {
    if (el.style) {
      el.style.margin = "0";
      el.style.padding = "0";
    }
  });

  Object.assign(clonedElement.style, {
    width: `${width}px`,
    height: `${height}px`,
    position: "relative",
    backgroundColor: EXPORT_CONFIG.backgroundColor,
    overflow: "hidden",
  });

  setupChartContainer(clonedElement, isRadialChart);
};

const setupChartContainer = (element, isRadialChart) => {
  const chartContainer = element.querySelector(".question-content");
  if (!chartContainer) return;

  chartContainer.style.cssText = "";
  Object.assign(chartContainer.style, {
    width: "100%",
    height: "100%",
    padding: isRadialChart
      ? `${EXPORT_CONFIG.topPadding / 2}px ${EXPORT_CONFIG.sidePadding}px ${
          EXPORT_CONFIG.bottomPadding
        }px`
      : `${EXPORT_CONFIG.topPadding}px ${EXPORT_CONFIG.sidePadding}px ${EXPORT_CONFIG.bottomPadding}px`,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  });

  if (isRadialChart) {
    chartContainer.style.marginTop = "-20px";
  }
};

const downloadImage = (canvas, title) => {
  const dataUrl = canvas.toDataURL("image/png");
  const link = document.createElement("a");
  const fileName = title.replace(/[^a-z0-9]/gi, "_").toLowerCase();
  link.download = `${fileName}.png`;
  link.href = dataUrl;
  link.click();
};
